import React from 'react';
import i18n from '../../../utilities/i18n';
import DynamicTable, { ColumnConfig } from '../../table/DynamicTable';
import { useQuery } from '@tanstack/react-query';
import { deviceUploadHistoryQueryOptions } from '../../../queryOptions/device';
import DateCellRenderer from '../../table/DateCellRenderer';
import { Table, Text } from '@adc/parallax-component-library';
import { StudyDataRecord } from '../../../models';
import { StudyDataProcessStatus } from 'adc-sirius-module-common';

type Props = {
  deviceId: string;
  showCard: boolean;
};

export const UploadHistoryTable = ({ deviceId, showCard }: Props) => {
  const getCombinedSystemNotes = (studyDataRecord: StudyDataRecord) => {
    return (studyDataRecord.systemNotes || [])
      .map((systemNote) => systemNote.note)
      .join('\n');
  };

  const columnData: ColumnConfig[] = [
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'processStatus',
      getSortValue: (record: StudyDataRecord) =>
        StudyDataProcessStatus[record.processStatus] || '',
    },
    {
      label: i18n.t('Entity.systemNotes'),
      dataKey: 'systemNotes',
      getSortValue: (record: StudyDataRecord) => getCombinedSystemNotes(record),
    },
  ];

  const cellRenderer = (dataKey: string, studyDataRecord: StudyDataRecord) => {
    if (dataKey === 'createdOn') {
      return (
        <DateCellRenderer value={studyDataRecord[dataKey] || 0} key={dataKey} />
      );
    } else if (dataKey === 'systemNotes') {
      const combinedSystemNotes = getCombinedSystemNotes(studyDataRecord);

      return (
        <Table.TextCell key={dataKey}>
          <Text type="$bodySmall.default">{combinedSystemNotes}</Text>
        </Table.TextCell>
      );
    } else if (dataKey === 'processStatus') {
      return (
        <Table.TextCell key={dataKey}>
          {StudyDataProcessStatus[studyDataRecord[dataKey]]}
        </Table.TextCell>
      );
    }
  };

  const deviceUploadRecords = useQuery(
    deviceUploadHistoryQueryOptions({
      key: deviceId,
    }),
  );

  return (
    <DynamicTable
      columnData={columnData}
      cellRenderer={cellRenderer}
      rowData={deviceUploadRecords.data || []}
      isLoading={deviceUploadRecords.isPending}
      hasLoadingError={deviceUploadRecords.isError}
      title={showCard ? i18n.t('Page_Sensor.upload_history_title') : ''}
      description={i18n.t('Page_Sensor.upload_history_description')}
      showCard={showCard}
    />
  );
};
